import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { homeContent } from '@/mocks/content';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
          <a
            href="/"
            className="font-heading text-xl md:text-2xl font-bold text-foreground-950 whitespace-nowrap cursor-pointer"
          >
            YOYBUY<span className="text-accent-500">.my</span>
          </a>

          <div className="hidden md:flex items-center gap-6">
            <a
              href="/"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              Главная
            </a>
            <Link
              to="/article/yoybuy-spreadsheet-guide"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              Руководство по покупкам
            </Link>
            <a href="#categories" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              Категории
            </a>
            <a href="#about" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              О системе
            </a>
            <a href="#blog" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
              Блог
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {homeContent.navDropdownLabel}
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-2xl border border-background-200/60 py-2 max-h-80 overflow-y-auto"
                  style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}
                >
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-accent-50 hover:text-accent-700 transition-colors border-l-2 border-transparent hover:border-accent-500 cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <span className="w-4 h-4 flex items-center justify-center ml-1.5">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute right-0 top-0 h-full w-[85vw] max-w-[380px] bg-background-50 overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200/60">
              <span className="font-heading text-lg font-bold text-foreground-950">Меню</span>
              <button
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-4 flex flex-col gap-1">
              <a
                href="/"
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
                onClick={() => setMobileOpen(false)}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-4-line text-base"></i>
                </span>
                Главная
              </a>
              <Link
                to="/article/yoybuy-spreadsheet-guide"
                onClick={() => {
                  setMobileOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-open-line text-base"></i>
                </span>
                Руководство по покупкам
              </Link>
            </div>

            <div className="px-4 pt-2 pb-1">
              <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400 px-3">Категории товаров</span>
            </div>

            <div className="px-4 pb-4">
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl hover:bg-accent-50 transition-colors cursor-pointer text-center"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-100">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground-800 leading-tight">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="px-4 pb-4 border-t border-background-200/60 pt-4">
              <a href="#categories" className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer" onClick={() => setMobileOpen(false)}>Категории</a>
              <a href="#about" className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer" onClick={() => setMobileOpen(false)}>О системе</a>
              <a href="#blog" className="block px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer" onClick={() => setMobileOpen(false)}>Блог</a>
            </div>

            <div className="px-4 pb-8">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block w-full px-5 py-3.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold text-center hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <span className="w-4 h-4 inline-flex items-center justify-center ml-1.5">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}